/**
 * NOS-239: site-wide announcement bar — a promo strip rendered ABOVE the nav
 * (inside PublicNavBar's sticky wrapper) on every public page. It is chrome,
 * like nav/footer — never a page section.
 *
 * - Colors default from brand tokens: background = primary color, text color
 *   auto-picked for WCAG contrast (announcementTextColor).
 * - Dismiss persists per visitor via localStorage; the key hashes the text so
 *   a new promo resurfaces the bar. Read synchronously in the useState
 *   initializer, so a dismissed bar never flashes (no layout shift on load).
 * - Builder canvas: selectable via the synthetic section id "__announcement__"
 *   (the "__nav__" pattern); dismiss is disabled there.
 */
import { useState } from "react"
import { X } from "lucide-react"
import {
  announcementDismissKey,
  announcementTextColor,
  type AnnouncementConfig,
} from "@/lib/announcement"

export const ANNOUNCEMENT_SECTION_ID = "__announcement__"

export interface AnnouncementBarProps {
  announcement: AnnouncementConfig | null | undefined
  primaryColor: string
  fontFamily: string
  /** Public site org id — keys dismiss persistence. Omit in the builder. */
  orgId?: string
  /** Builder canvas: bar is clickable for selection, dismiss disabled. */
  selectable?: boolean
  selectedSectionId?: string | null
  onSectionClick?: (sectionId: string, e: React.MouseEvent) => void
}

export function AnnouncementBar({
  announcement,
  primaryColor,
  fontFamily,
  orgId,
  selectable = false,
  selectedSectionId = null,
  onSectionClick,
}: AnnouncementBarProps) {
  const inBuilder = selectable || !orgId
  const storageKey =
    announcement && orgId ? announcementDismissKey(orgId, announcement) : null
  const [dismissed, setDismissed] = useState(() => {
    if (inBuilder || !storageKey || typeof localStorage === "undefined") return false
    try {
      return localStorage.getItem(storageKey) === "1"
    } catch {
      return false
    }
  })

  if (!announcement || !announcement.enabled || !announcement.text || dismissed) return null

  const background = announcement.background ?? primaryColor
  const color = announcementTextColor(background)
  const dismissible = announcement.dismissible !== false

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (inBuilder) return
    setDismissed(true)
    try {
      if (storageKey) localStorage.setItem(storageKey, "1")
    } catch {
      /* private mode — the bar just reappears next load */
    }
  }

  const linkStyle: React.CSSProperties = {
    color,
    fontWeight: 700,
    textDecoration: "underline",
    textUnderlineOffset: 2,
    whiteSpace: "nowrap",
  }

  return (
    <div
      role={selectable ? "button" : "region"}
      aria-label={selectable ? undefined : "Announcement"}
      tabIndex={selectable ? 0 : undefined}
      data-testid="announcement-bar"
      onClick={selectable ? (e) => onSectionClick?.(ANNOUNCEMENT_SECTION_ID, e) : undefined}
      onKeyDown={
        selectable
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                onSectionClick?.(ANNOUNCEMENT_SECTION_ID, e as unknown as React.MouseEvent)
              }
            }
          : undefined
      }
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        padding: "8px 44px",
        background,
        color,
        fontFamily,
        fontSize: 13,
        lineHeight: 1.45,
        textAlign: "center",
        cursor: selectable ? "pointer" : undefined,
        outline:
          selectedSectionId === ANNOUNCEMENT_SECTION_ID ? `2px solid ${primaryColor}` : "none",
        outlineOffset: -2,
        transition: "outline 0.15s",
      }}
    >
      {selectedSectionId === ANNOUNCEMENT_SECTION_ID && (
        <div
          style={{
            position: "absolute",
            top: 4,
            left: 10,
            padding: "2px 7px",
            background: primaryColor,
            color: "#fff",
            fontSize: 9,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.06em",
            borderRadius: 5,
            zIndex: 10,
            fontFamily: "'DM Sans', sans-serif",
          }}
        >
          Announcement
        </div>
      )}
      {announcement.link_href && !announcement.link_label ? (
        // Whole-message link (imported bars that were one big anchor).
        // Builder: display-only (no navigation from the canvas).
        inBuilder ? (
          <span style={{ ...linkStyle, fontWeight: 500, whiteSpace: "normal" }}>
            {announcement.text}
          </span>
        ) : (
          <a
            href={announcement.link_href}
            style={{ ...linkStyle, fontWeight: 500, whiteSpace: "normal" }}
          >
            {announcement.text}
          </a>
        )
      ) : (
        <span style={{ fontWeight: 500 }}>{announcement.text}</span>
      )}
      {announcement.link_href && announcement.link_label && (
        // Builder: display-only (no navigation from the canvas)
        inBuilder ? (
          <span style={linkStyle}>{announcement.link_label}</span>
        ) : (
          <a href={announcement.link_href} style={linkStyle}>
            {announcement.link_label}
          </a>
        )
      )}
      {dismissible && !inBuilder && (
        <button
          type="button"
          aria-label="Dismiss announcement"
          onClick={handleDismiss}
          style={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            background: "transparent",
            border: "none",
            borderRadius: 6,
            color,
            opacity: 0.7,
            cursor: "pointer",
          }}
        >
          <X size={15} />
        </button>
      )}
    </div>
  )
}
